import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { ROVER_MOS_DEVICE_ID } from '@src/suite/common/enums/constants';
import { MosEventService } from '@src/suite/common/services/mos-event.service';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { SidecarEfffectActions } from '@src/suite/common/store/fullscreen';
import { selectRecordToEdit, selectSidecarSelectedMosPluginPendingCommand, selectStoryRecordsToEdit, selectStoryRecordToEdit } from '@src/suite/common/store/fullscreen/selectors/sidecar.selectors';
import { selectHomeDatabase } from '@src/suite/common/store/hello-results/hello-results.selectors';
import { MosActions } from '@src/suite/common/store/mos/actions/mos.actions';
import { StoryMosActions } from '@src/suite/common/store/story/actions/story.actions';
import { convertMosItemFromPlugin, MosCommand, MosDescriptions, ncsAppInfoResponse, ncsErrorResponse, ncsItemRequestNotFoundResponse, ncsItemRequestResponse } from '@src/suite/common/utils/mos-utlis';
import { exhaustMap, map, of } from 'rxjs';

import { ApplicationActions } from '../../navigation/actions/application.actions';
import { RecordMosActions } from '../actions/record-mos.actions';

@Injectable()
export class MosEffects {
    private actions$ = inject(Actions);
    private store = inject(Store);

    private nomWebServicesJson = inject(NomWebServicesJson);
    private mosEventService = inject(MosEventService);

    handleClientMosCommand$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(MosActions.mosClientCommandSend),
                map(({ command, mosPluginId, sidecar }) => {
                    this.mosEventService.emitMosClientCommand({
                        command,
                        mosPluginId,
                        sidecar,
                    });
                })
            );
        },
        { dispatch: false }
    );

    handleMosCommand$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MosActions.mosServerCommandSend),
            concatLatestFrom(() => [this.store.select(selectStoryRecordToEdit), this.store.select(selectHomeDatabase)]),
            exhaustMap(([action, story, ncsId]) => {
                const mosCommand = action.mosCommandType;
                switch (mosCommand) {
                    case MosCommand.ncsReqAppInfo: {
                        const actions = [];

                        /**
                         * Mos response to the initial mos plugin command
                         */
                        const userName = action.mosPluginId.toLowerCase().includes(ROVER_MOS_DEVICE_ID.toLowerCase()) ? this.nomWebServicesJson.HelloResults.UserName : null;
                        actions.push(
                            MosActions.mosClientCommandSend({
                                command: ncsAppInfoResponse({
                                    mosDeviceId: action.mosPluginId,
                                    userId: this.nomWebServicesJson.UserId,
                                    userName: userName,
                                    ncsId: ncsId,
                                    viewPort: {
                                        x: window.visualViewport?.width || window.innerWidth,
                                        y: window.visualViewport?.height || window.innerHeight,
                                    },
                                }),
                                sidecar: action.sidecar,
                                mosPluginId: action.mosPluginId,
                            })
                        );
                        /**
                         * We need to know when the mos plugin is initialized,
                         * so we can open the mos item in the specified sidecar, if there is one.
                         */
                        actions.push(SidecarEfffectActions.setCurrentMosPluginInitialized({ mosPluginId: action.mosPluginId, sidecar: action.sidecar }));

                        /**
                         * When we use 'SidecarEfffectActions.openMosItem' and the mos plugin is not yet initialized,
                         * we store the command and wait for the plugin to initialize.
                         * Now we can open the pending mos item, in the specified sidecar, if there is one.
                         * Using 'SidecarEfffectActions.openPendingMosItem' instead of 'MosActions.mosClientCommandSend' directly,
                         * because we want to clear the pending command before opening the mos item.
                         *
                         * We will end up here probably after 'SidecarEfffectActions.openMosItemInSidecar' only
                         */
                        const pendingClientCommand = this.store.selectSignal(selectSidecarSelectedMosPluginPendingCommand(action.sidecar))();
                        if (pendingClientCommand) {
                            actions.push(SidecarEfffectActions.openPendingMosItem({ mosItemCommand: pendingClientCommand, sidecar: action.sidecar, mosPluginId: action.mosPluginId }));
                        }
                        return actions;
                    }
                    case MosCommand.ncsItem:
                        return of(StoryMosActions.addMosItem({ storyId: story?.Guid, mosStr: convertMosItemFromPlugin(action.command), mosPluginId: action.mosPluginId }));
                    case MosCommand.ncsItemRequest: {
                        const storyRecords = this.store.selectSignal(selectStoryRecordsToEdit)();
                        if (!storyRecords || storyRecords.length === 0) {
                            console.warn('No story records found for ncsItemRequest command');
                            return of(MosActions.mosClientCommandSend({ command: ncsItemRequestNotFoundResponse(), sidecar: action.sidecar, mosPluginId: action.mosPluginId }));
                        }

                        const currentMirContext = this.mosEventService.currentMirStatus;

                        if (!currentMirContext) {
                            console.warn('No current MIR context found for ncsItemRequest command');
                            return of(
                                MosActions.mosClientCommandSend({ command: ncsErrorResponse(MosDescriptions.StatusDescriptionUnrecognized), sidecar: action.sidecar, mosPluginId: action.mosPluginId })
                            );
                        }

                        const storyRecord = storyRecords.find(record => record.Guid === currentMirContext?.storyId);

                        if (!storyRecord) {
                            console.warn('No story record found for ncsItemRequest command', currentMirContext.storyId);
                            return of(
                                MosActions.mosClientCommandSend({ command: ncsErrorResponse(MosDescriptions.StatusDescriptionUnrecognized), sidecar: action.sidecar, mosPluginId: action.mosPluginId })
                            );
                        }

                        if (!currentMirContext.mosCommand || !currentMirContext.mirItemId) {
                            console.warn('No mosCommand or MIR item ID found in current MIR context for ncsItemRequest command', currentMirContext.storyId);
                            return of(
                                MosActions.mosClientCommandSend({ command: ncsErrorResponse(MosDescriptions.StatusDescriptionUnrecognized), sidecar: action.sidecar, mosPluginId: action.mosPluginId })
                            );
                        }

                        return of(MosActions.mosClientCommandSend({ command: ncsItemRequestResponse(currentMirContext.mosCommand), sidecar: action.sidecar, mosPluginId: action.mosPluginId }));
                    }
                    case MosCommand.mosItemReplace: {
                        /*
                        Hint: Potential issue, if the replace command is not for the same story as the current one.
                        We need to check this before replacing the item, otherwise we might end up with a wrong story being updated.
                        Not sure if this is a real issue, or if we care about this case.
                        TODO: we need to check this case.
                        */
                        const currentMirContext = this.mosEventService.currentMirStatus;
                        if (!currentMirContext?.storyId || !currentMirContext.mirItemId) {
                            console.warn('No current MIR context found for mosItemReplace command');
                            return of(ApplicationActions.noOpAction());
                        }

                        const storyRecords = this.store.selectSignal(selectStoryRecordsToEdit)();
                        const storyRecord = storyRecords.find(record => record.Guid === currentMirContext?.storyId);
                        if (!storyRecord) {
                            console.warn('No story record found for current MIR context in mosItemReplace command');
                            return of(ApplicationActions.noOpAction());
                        }

                        return of(
                            StoryMosActions.replaceMosItem({
                                storyId: currentMirContext.storyId,
                                mosStr: convertMosItemFromPlugin(action.command),
                                mosItemId: currentMirContext.mirItemId,
                                mosPluginId: action.mosPluginId,
                            })
                        );
                    }
                    case MosCommand.ncsStoryRequest: {
                        // find the story record and send it to the plugin
                        const storyRecord = (this.store.selectSignal(selectStoryRecordsToEdit)() ?? [])?.find(record => record.Guid === this.mosEventService.currentMirStatus?.storyId);

                        if (storyRecord != null) {
                            return of(StoryMosActions.requestMOSStoryRecord({ sidecar: action.sidecar, mosPluginId: action.mosPluginId, story: storyRecord }));
                        }

                        // Fallback to the current opened fullscreen record (this is needed for the roverAI)
                        const currentRecordToEdit = this.store.selectSignal(selectRecordToEdit)();
                        if (currentRecordToEdit == null) {
                            return of(
                                MosActions.mosClientCommandSend({ command: ncsErrorResponse(MosDescriptions.StatusDescriptionUnrecognized), sidecar: action.sidecar, mosPluginId: action.mosPluginId })
                            );
                        }
                        return of(RecordMosActions.requestMOSRecord({ sidecar: action.sidecar, mosPluginId: action.mosPluginId, record: currentRecordToEdit }));

                        /*
                        // find the story record and send it to the plugin
                        const storyRecords = this.store.selectSignal(selectStoryRecordsToEdit)();
                        if (storyRecords == null || storyRecords.length === 0) {
                            // Fallback to the current opened fullscreen record (this is needed for the roverAI)
                            if (currentRecordToEdit == null) {
                                return of(
                                    MosActions.mosClientCommandSend({
                                        command: ncsErrorResponse(MosDescriptions.StatusDescriptionUnrecognized),
                                        sidecar: action.sidecar,
                                        mosPluginId: action.mosPluginId,
                                    })
                                );
                            }
                            return of(RecordMosActions.requestMOSRecord({ sidecar: action.sidecar, mosPluginId: action.mosPluginId, record: currentRecordToEdit }));
                        }

                        const storyRecord = storyRecords.find(record => record.Guid === this.mosEventService.currentMirStatus?.storyId);

                        if (storyRecord) {
                            return of(StoryMosActions.requestMOSStoryRecord({ sidecar: action.sidecar, mosPluginId: action.mosPluginId, story: storyRecord }));
                        }

                        // Fallback to the current opened fullscreen record (this is needed for the roverAI)
                        if (currentRecordToEdit == null) {
                            return of(
                                MosActions.mosClientCommandSend({ command: ncsErrorResponse(MosDescriptions.StatusDescriptionUnrecognized), sidecar: action.sidecar, mosPluginId: action.mosPluginId })
                            );
                        }
                        return of(RecordMosActions.requestMOSRecord({ sidecar: action.sidecar, mosPluginId: action.mosPluginId, record: storyRecord }));
                        */
                    }
                    case MosCommand.ncsReqStoryActionNew:
                        return of(StoryMosActions.requestNewMOSStory({ mosStr: action.command, mosPluginId: action.mosPluginId, sidecar: action.sidecar }));
                    case MosCommand.ncsReqStoryActionUpdate:
                        console.info('TODO Story action update');
                        break;
                    case MosCommand.ncsReqStoryActionReplace:
                        console.info('TODO Story action replace');
                        break;
                }
                return of(MosActions.mosClientCommandSend({ command: '', sidecar: action.sidecar, mosPluginId: action.mosPluginId }));
            })
        );
    });
}

/*
When Mos plugin send mosItemReplace, if the story doesn't match to the current one, send ncsAck with error message. If the story matches, replace the item and send ncsAck with success message.
*/
